import React from 'react';

// complete_details_required_28sep2026 — shared required-details block:
// first name + surname (two boxes), DOB (DD/MM/YYYY, 18+), email,
// phone, company/salon where applicable, full address (UK: street,
// town/city, postcode; other regions: street, area/city, state or
// country). Every missing field highlights with its own message and
// submit stays blocked until complete.

export const maxDob18 = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10); // yyyy-mm-dd for <input type=date>
};

export const dobToDisplay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export const validateAccountForm = (v, { region = 'uk', companyRequired = false, requireEmail = true } = {}) => {
  const errs = {};
  if (!String(v.first_name || '').trim()) errs.first_name = 'Enter a first name.';
  if (!String(v.last_name || '').trim()) errs.last_name = 'Enter a surname.';
  if (!String(v.date_of_birth || '').trim()) {
    errs.date_of_birth = 'Enter a date of birth (DD/MM/YYYY).';
  } else {
    const dob = new Date(v.date_of_birth);
    const cutoff = new Date(); cutoff.setFullYear(cutoff.getFullYear() - 18);
    if (isNaN(dob.getTime())) errs.date_of_birth = 'Enter a valid date of birth as DD/MM/YYYY.';
    else if (dob > cutoff) errs.date_of_birth = 'This platform is for professionals aged 18 and over.';
  }
  if (requireEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v.email || '').trim())) errs.email = 'Enter a valid email address.';
  if ((String(v.phone || '').replace(/\D/g, '')).length < 7) errs.phone = 'Enter a phone number.';
  if (companyRequired && !String(v.company || v.salon_name || '').trim()) errs.company = 'Enter the company or salon name.';
  if (!String(v.address || '').trim()) errs.address = 'Enter the street address line.';
  if (!String(v.city || '').trim()) errs.city = 'Enter the town or city.';
  if ((region || 'uk').toLowerCase() === 'uk') {
    if (!String(v.postcode || '').trim()) errs.postcode = 'Enter the postcode.';
  } else if (!String(v.state || v.country || '').trim()) {
    errs.state = 'Enter the state or country.';
  }
  return errs;
};

const Field = ({ label, error, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-[#4A4A4A] block">{label} <span className="text-red-500">*</span></label>
    {children}
    {error && <p className="text-[11px] text-red-600" data-testid={`field-error-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{error}</p>}
  </div>
);

const cls = (err) => `w-full h-10 px-3 rounded-md border text-sm bg-white ${err ? 'border-red-500 ring-1 ring-red-300' : 'border-gray-300'}`;

export const AccountDetailsFields = ({ values, onChange, errors = {}, region = 'uk', showCompany = false, companyLabel = 'Company / Salon name', showEmail = true }) => {
  const set = (k) => (e) => onChange({ ...values, [k]: e.target.value });
  const isUk = (region || 'uk').toLowerCase() === 'uk';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="account-details-fields">
      <Field label="First name" error={errors.first_name}>
        <input className={cls(errors.first_name)} value={values.first_name || ''} onChange={set('first_name')} data-testid="acct-first-name" />
      </Field>
      <Field label="Surname" error={errors.last_name}>
        <input className={cls(errors.last_name)} value={values.last_name || ''} onChange={set('last_name')} data-testid="acct-last-name" />
      </Field>
      <Field label="Date of birth" error={errors.date_of_birth}>
        <input type="date" max={maxDob18()} className={cls(errors.date_of_birth)} value={values.date_of_birth || ''} onChange={set('date_of_birth')} data-testid="acct-dob" />
      </Field>
      <Field label="Phone" error={errors.phone}>
        <input className={cls(errors.phone)} value={values.phone || ''} onChange={set('phone')} data-testid="acct-phone" />
      </Field>
      {showEmail && (
        <Field label="Email" error={errors.email}>
          <input type="email" className={cls(errors.email)} value={values.email || ''} onChange={set('email')} data-testid="acct-email" />
        </Field>
      )}
      {showCompany && (
        <Field label={companyLabel} error={errors.company}>
          <input className={cls(errors.company)} value={values.company || ''} onChange={set('company')} data-testid="acct-company" />
        </Field>
      )}
      <div className="sm:col-span-2">
        <Field label="Street address" error={errors.address}>
          <input className={cls(errors.address)} value={values.address || ''} onChange={set('address')} data-testid="acct-address" />
        </Field>
      </div>
      <Field label={isUk ? 'Town / City' : 'Area / City'} error={errors.city}>
        <input className={cls(errors.city)} value={values.city || ''} onChange={set('city')} data-testid="acct-city" />
      </Field>
      {isUk ? (
        <Field label="Postcode" error={errors.postcode}>
          <input className={cls(errors.postcode)} value={values.postcode || ''} onChange={set('postcode')} data-testid="acct-postcode" />
        </Field>
      ) : (
        <Field label="State / Country" error={errors.state}>
          <input className={cls(errors.state)} value={values.state || ''} onChange={set('state')} data-testid="acct-state" />
        </Field>
      )}
    </div>
  );
};

// Payload the shared backend validator expects (DOB sent as DD/MM/YYYY).
export const accountPayload = (v) => ({
  first_name: (v.first_name || '').trim(),
  last_name: (v.last_name || '').trim(),
  date_of_birth: dobToDisplay(v.date_of_birth),
  phone: (v.phone || '').trim(),
  email: (v.email || '').trim(),
  address: (v.address || '').trim(),
  city: (v.city || '').trim(),
  postcode: (v.postcode || '').trim(),
  state: (v.state || '').trim(),
  country: (v.country || '').trim(),
});
